/**
 * Terms & Privacy checkbox. Country-aware copy:
 *   - CA: dual English / French wording with two link pairs.
 *   - Anywhere else: single "I accept …" line.
 *
 * The form gates its submit button on `checked === true`.
 */

import React from 'react';
import { Box, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';

import { COUNTRY_CODES, TERMS_CONTENT, UI_CONFIG } from '../../utils/constants';
import {
  getPrivacyAndCollectionByCountry,
  getTermsAndConditionsByCountry,
} from '../../utils/redirect';

export interface TermsCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  country: string;
}

const TermsCheckbox: React.FC<TermsCheckboxProps> = ({ checked, onChange, country }) => {
  const upper = country?.toUpperCase();
  const formControlLabelSx = {
    alignItems: 'flex-start',
    m: 0,
    width: '100%',
    '.MuiCheckbox-root': {
      alignSelf: 'flex-start',
      mr: 1.5,
      p: 0,
      pt: '2px',
    },
    '.MuiFormControlLabel-label': {
      flex: 1,
      display: 'block',
      mt: '2px',
      textAlign: 'justify',
      textJustify: 'inter-word',
    },
  } as const;

  if (upper === COUNTRY_CODES.CA) {
    return (
      <FormControlLabel
        sx={formControlLabelSx}
        control={
          <Checkbox
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            sx={{
              color: 'white',
              '&.MuiCheckbox-root.Mui-checked': { color: 'white' },
            }}
          />
        }
        label={
          <Box component="span" sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, fontSize: '0.87rem' }}>
            {TERMS_CONTENT.CA.ACCEPTANCE_TEXT}
            <br />
            <br />
            <b>{TERMS_CONTENT.CA.READ_IN_ENGLISH}</b>
            <br />
            <Link
              href={getTermsAndConditionsByCountry.CA}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, textDecoration: 'underline' }}
            >
              {TERMS_CONTENT.CA.TERMS_LINK_TEXT_EN}
            </Link>
            {' '}
            {TERMS_CONTENT.CA.AND_TEXT_EN}
            {' '}
            <Link
              href={getPrivacyAndCollectionByCountry.CA}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, textDecoration: 'underline' }}
            >
              {TERMS_CONTENT.CA.PRIVACY_LINK_TEXT_EN}
            </Link>
            <br />
            <br />
            <b>{TERMS_CONTENT.CA.READ_IN_FRENCH}</b>
            <br />
            <Link
              href={getTermsAndConditionsByCountry.CA_FR}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, textDecoration: 'underline' }}
            >
              {TERMS_CONTENT.CA.TERMS_LINK_TEXT_FR}
            </Link>
            {' '}
            {TERMS_CONTENT.CA.AND_TEXT_FR}
            {' '}
            <Link
              href={getPrivacyAndCollectionByCountry.CA_FR}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, textDecoration: 'underline' }}
            >
              {TERMS_CONTENT.CA.PRIVACY_LINK_TEXT_FR}
            </Link>
          </Box>
        }
      />
    );
  }

  const termsKey = (upper ?? 'US') as keyof typeof getTermsAndConditionsByCountry;
  const termsUrl = getTermsAndConditionsByCountry[termsKey] ?? getTermsAndConditionsByCountry.US;
  const privacyUrl =
    getPrivacyAndCollectionByCountry[termsKey] ?? getPrivacyAndCollectionByCountry.US;

  return (
    <FormControlLabel
      sx={formControlLabelSx}
      control={
        <Checkbox
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          sx={{
            color: 'white',
            '&.MuiCheckbox-root.Mui-checked': { color: 'white' },
          }}
        />
      }
      label={
        <Typography
          component="span"
          sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, fontSize: '0.87rem' }}
        >
          {TERMS_CONTENT.DEFAULT.ACCEPTANCE_TEXT}
          {' '}
          <Link
            href={termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, textDecoration: 'underline' }}
          >
            {TERMS_CONTENT.DEFAULT.TERMS_LINK_TEXT}
          </Link>
          {' '}
          {TERMS_CONTENT.DEFAULT.AND_TEXT}
          {' '}
          <Link
            href={privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, textDecoration: 'underline' }}
          >
            {TERMS_CONTENT.DEFAULT.PRIVACY_LINK_TEXT}
          </Link>
        </Typography>
      }
    />
  );
};

export default TermsCheckbox;
